/**
 * HR Data Service -- warstwa danych modulu HR (API backend).
 *
 * Zastapienie localStorage na wywolania API. Kazda funkcja jest async.
 * Funkcje ktore nie maja jeszcze endpointu backendowego (evaluations,
 * vacation plans, work schedules, notifications) sa oznaczone jako stuby
 * i zwracaja puste dane -- do migracji w kolejnej fazie.
 *
 * Eksportowane nazwy funkcji sa identyczne z poprzednia wersja,
 * dzieki czemu konsumenci (pages/components) nie wymagaja zmian importow.
 */

import apiService from "../api/apiService";

// [P1] Domyślne okno wycofania ZREALIZOWANEGO wniosku, gdy typ nie ma własnej wartości.
// Lustro stałej serwera: HrWithdrawalPolicy.DomyslneOknoDni.
const DOMYSLNE_OKNO_WYCOFANIA_DNI = 14;

// ---- Rejestrator interakcji HR (audyt zdarzen, wysylka wsadowa) ---------

const INTERACTION_FLUSH_SIZE = 12;   // wyslij od razu po tylu zdarzeniach
const INTERACTION_FLUSH_MS = 5000;   // albo po tylu ms bezczynnosci
const hrInteractionBuffer = [];
let hrInteractionTimer = null;

// Identyfikator sesji karty (do korelacji zdarzen jednej wizyty). Best-effort.
const getHrSessionId = () => {
    try {
        let sid = sessionStorage.getItem("hr_log_sid");
        if (!sid) {
            sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
            sessionStorage.setItem("hr_log_sid", sid);
        }
        return sid;
    } catch { return null; }
};

const safeStringify = (obj) => {
    try { return JSON.stringify(obj || {}).slice(0, 2000); }
    catch { return "{}"; }
};

const flushHrInteractions = () => {
    if (hrInteractionTimer) { clearTimeout(hrInteractionTimer); hrInteractionTimer = null; }
    if (hrInteractionBuffer.length === 0) return;
    const events = hrInteractionBuffer.splice(0, hrInteractionBuffer.length);
    // Wysylka best-effort — porazka nie wraca do UI (event i tak jest miekki).
    try {
        apiService.logHrInteractions({ sessionId: getHrSessionId(), events })
            .catch(() => { /* offline / 401 — pomijamy */ });
    } catch { /* brak sesji/tokenu */ }
};

const scheduleHrInteractionFlush = () => {
    if (hrInteractionTimer) return;
    hrInteractionTimer = setTimeout(flushHrInteractions, INTERACTION_FLUSH_MS);
};

// Domknij bufor gdy karta znika (przelaczenie/zamkniecie) — nie gubimy ostatnich zdarzen.
if (typeof window !== "undefined") {
    window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flushHrInteractions();
    });
    window.addEventListener("pagehide", flushHrInteractions);
}

// ---- Zakładki statusu: jedno źródło prawdy (liczniki kafelków == filtr listy) ----
// Zakładka UI -> lista realnych statusów backendu (enum HrRequestStatus). Backend GetRequestsAsync
// obsługuje listę oddzieloną przecinkami; getStats liczy kafelki po TYCH SAMYCH zbiorach.
// „Oczekujące" = wszystko w toku (Pending* + ApprovedBy* czekające na kolejny krok + Resubmitted).
export const HR_TAB_STATUSES = {
    pending: [
        "PendingManager", "PendingDirector", "PendingHr", "PendingCfo", "PendingPresident",
        "PendingEmployeeAck", "ApprovedByManager", "ApprovedByDirector", "ApprovedByCfo",
        "ApprovedByPresident", "Resubmitted",
    ],
    corrections: ["CorrectionRequested"],
    approved: ["Completed"],
    rejected: ["Rejected", "RejectedByManager", "RejectedByDirector", "RejectedByCfo", "RejectedByPresident"],
};

// Zwraca string statusów po przecinku dla danej zakładki (albo null dla „Wszystkie"/nieznanej).
export const tabToStatusParam = (tabId) =>
    (tabId && HR_TAB_STATUSES[tabId]) ? HR_TAB_STATUSES[tabId].join(",") : null;

// ---- Pure utility helpers (no data, no storage) -------------------------

const isDirector = (user) => {
    if (!user) return false;
    if (user.jobPosition >= 60) return true;
    if (user.role === "director") return true;
    const pos = (user.position || "").toLowerCase();
    return pos.includes("dyrektor");
};

/**
 * Maps backend HrRequestDto (with formDataJson string) to the shape
 * the frontend expects (with formData object).
 */
const mapRequest = (dto) => {
    if (!dto) return null;
    const mapped = { ...dto };

    // Backend field: typeCode -> frontend field: typeId
    if (mapped.typeCode && !mapped.typeId) {
        mapped.typeId = mapped.typeCode;
    }

    // Backend field: categoryCode -> frontend field: category
    if (mapped.categoryCode && !mapped.category) {
        mapped.category = mapped.categoryCode;
    }

    // Backend field: limitPoolCode -> frontend field: limitPool
    if (mapped.limitPoolCode && !mapped.limitPool) {
        mapped.limitPool = mapped.limitPoolCode;
    }

    // Backend sends formDataJson as string, frontend uses formData as object
    if (typeof mapped.formDataJson === "string") {
        try {
            mapped.formData = JSON.parse(mapped.formDataJson);
        } catch {
            mapped.formData = {};
        }
        delete mapped.formDataJson;
    }
    if (!mapped.formData && !mapped.formDataJson) {
        mapped.formData = {};
    }

    // [brief właściciela 2026-08-30, pkt 3] SPÓŁKA-ADRESAT zamrożona w chwili składania —
    // ta sama, z której dokument bierze nazwę, adres i logo. Czytamy ją ze snapshotu, a nie
    // z dzisiejszej tabeli firm, żeby ekran mówił to samo, co pismo w aktach.
    if (typeof mapped.documentSnapshotJson === "string" && mapped.documentSnapshotJson) {
        try {
            const snap = JSON.parse(mapped.documentSnapshotJson);
            mapped.documentSnapshot = snap;
            if (snap?.company?.name) mapped.companyName = snap.company.name;
        } catch { mapped.documentSnapshot = null; }
    }

    // Ensure timeline array exists
    if (!mapped.timeline) mapped.timeline = [];

    // Normalize status to string (backend enum -> string like "PendingManager")
    if (mapped.status != null && typeof mapped.status === "number") {
        // numeric enum fallback — shouldn't happen with proper serialisation
        mapped.status = String(mapped.status);
    }

    return mapped;
};

/**
 * Maps a list result from backend { items, totalCount } applying mapRequest.
 */
const mapListResult = (data) => {
    if (!data) return { items: [], totalCount: 0 };
    return {
        items: (data.items || []).map(mapRequest),
        totalCount: data.totalCount ?? data.total ?? 0,
        page: data.page,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
    };
};

/**
 * Maps backend HrBalanceDto[] to the legacy { [poolCode]: { total, used, pending }, year } shape.
 *
 * [brief 2026-08-31, pkt B] TA WARSTWA GUBIŁA POŁOWĘ ODPOWIEDZI SERWERA. Zostawiała wyłącznie
 * total/used/pending, więc każdy ekran musiał liczyć „pozostało" sam — a e-teczka liczyła je jako
 * total − used − pending, czyli BEZ dni z Systemu B (zatwierdzone zakresy kalendarza). Skutek:
 * dla tej samej osoby akta pokazywały inną liczbę dni niż /moj-czas-pracy i /moj-dzial/urlopy.
 * Przepuszczamy więc `effectiveRemaining` (jedyne kanoniczne „pozostało"), `isAssigned` (pula
 * ODPIĘTA pracownikowi nie może wyglądać jak przysługująca) oraz podpowiedź ustawową.
 * Kształt mapy zostaje bez zmian — dochodzą wyłącznie pola, więc żaden konsument nie traci nic.
 */
const mapBalances = (balanceDtos) => {
    if (!balanceDtos || !Array.isArray(balanceDtos)) return { year: new Date().getFullYear() };
    const result = {};
    let year = new Date().getFullYear();
    balanceDtos.forEach((b) => {
        // total = LIMIT EFEKTYWNY/egzekwowany = proratedTotal (proporcja etatu/miesięcy + baza z wariantu umowy);
        // fallback do surowego total dla starych odpowiedzi. proratedTotal/baseTotal zachowane osobno.
        const prorated = b.proratedTotal ?? b.total ?? 0;
        result[b.poolCode] = {
            poolCode: b.poolCode,
            poolLabel: b.poolLabel ?? null,
            total: prorated,
            proratedTotal: prorated,
            baseTotal: b.baseTotal ?? b.total ?? 0,
            rawTotal: b.total ?? 0,
            used: b.used ?? 0,
            pending: b.pending ?? 0,
            // „Pozostało" liczy SERWER. remaining = limit − wnioski, effectiveRemaining dodatkowo
            // odejmuje dni Systemu B. Fallback tylko dla starych odpowiedzi bez tych pól.
            remaining: b.remaining ?? Math.max(0, prorated - (b.used ?? 0) - (b.pending ?? 0)),
            systemBDays: b.systemBDays ?? 0,
            effectiveRemaining: b.effectiveRemaining
                ?? b.remaining
                ?? Math.max(0, prorated - (b.used ?? 0) - (b.pending ?? 0)),
            isAssigned: b.isAssigned !== false,
            monthsWorked: b.monthsWorked ?? 12,
            dimensionFactor: b.dimensionFactor ?? 1,
            // Podpowiedź kodeksowa — wyłącznie informacja, nie wchodzi do żadnego wyliczenia.
            statutoryTotal: b.statutoryTotal ?? null,
            statutoryNote: b.statutoryNote ?? null,
            belowStatutory: Boolean(b.belowStatutory),
            serviceYears: b.serviceYears ?? null,
        };
        if (b.year) year = b.year;
    });
    result.year = year;
    return result;
};

// =========================================================================
//                        PUBLIC API
// =========================================================================

const hrDataService = {

    // -- INITIALIZATION ---------------------------------------------------
    /**
     * Inicjalizacja -- na backendzie nie ma potrzeby seedowania.
     * Zachowana dla kompatybilnosci z konsumentami (np. hrDataService.init(user)).
     */
    async init(_user) {
        // No-op -- backend handles data initialisation.
    },

    // -- REQUESTS -- CRUD -------------------------------------------------

    /**
     * Pobiera liste wnioskow danego uzytkownika.
     * @param {string|number} userId
     * @param {object} filters -- { status, search, page, pageSize }
     */
    async getMyRequests(userId, filters = {}) {
        try {
            const params = {
                userId,
                status: filters.status,
                searchQuery: filters.search,
                pageNumber: filters.page || 1,
                pageSize: filters.pageSize || 20,
            };
            const response = await apiService.getHrRequests(params);
            return mapListResult(response.data);
        } catch (err) {
            console.error("[hrDataService] getMyRequests failed:", err);
            // FIX audytu 2026-08-29 (M-29): `error` odróżnia AWARIĘ od pustej listy — bez tego strona
            // pokazywała pracownikowi „Nie masz jeszcze żadnych wniosków HR", gdy API nie odpowiadało.
            // Ten sam wzorzec ma już getAllRequests (widok obsługi HR).
            return { items: [], totalCount: 0, error: true };
        }
    },

    /**
     * Pobiera szczegoly pojedynczego wniosku.
     */
    async getRequest(requestId, _user) {
        try {
            const response = await apiService.getHrRequest(requestId);
            return mapRequest(response.data);
        } catch (err) {
            console.error("[hrDataService] getRequest failed:", err);
            return null;
        }
    },

    /**
     * Tworzy i sklada nowy wniosek.
     * @param {object} options -- { companyId, assignmentId, evaluationNoteIds } (worek opcji; sygnatura sie nie zmienia)
     * @returns {object} utworzony wniosek
     */
    async createRequest(user, typeId, formData, attachments = [], options = {}) {
        try {
            // Pole userPicker 'targetEmployee' emituje OBIEKT {id,name}; wyłuskujemy numeryczne id do DTO,
            // inaczej TargetEmployeeId=null i wniosek "za pracownika" odlicza saldo/krok/dedup składającemu.
            const tgt = formData?.targetEmployee;
            const targetEmployeeId = (tgt && typeof tgt === "object" ? tgt.id : (tgt ?? formData?.targetEmployeeId)) || undefined;
            // [brief właściciela 2026-08-30, pkt 3] SPÓŁKA-ADRESAT wniosku. Backend waliduje ją
            // („Wskazana spółka nie jest spółką tego pracownika") i zamraża w snapshocie razem
            // z logo, ale front nigdy jej nie wysyłał — każdy wniosek szedł do pierwszej spółki
            // z listy, także u osoby zatrudnionej w dwóch. Pusto = serwer wybiera jak dotąd.
            const companyId = options?.companyId ?? formData?.companyId;
            // [brief właściciela 2026-08-30, pkt 4] ZLECENIE HR, w odpowiedzi na które składany jest wniosek.
            // Backend przyjmował to pole od początku (i po zapisie zamykał zlecenie), ale front go NIGDY nie
            // wysyłał — skierowanie zostawało otwarte na zawsze, a kadry nie miały jak zobaczyć, że sprawa
            // ruszyła. Pusto = wniosek z własnej inicjatywy, jak dotąd.
            const assignmentId = options?.assignmentId ?? undefined;
            // [brief właściciela 2026-08-30] NOTATKI PRZEŁOŻONEGO dołączane do wniosku o ocenę.
            // Wysyłamy SAME IDENTYFIKATORY — treść notatki bierze serwer z dziennika, więc na dokumencie
            // ląduje to, co przełożony naprawdę zapisał, a nie tekst z przeglądarki. Notatki niewskazane
            // zostają prywatne („mogę dodać notatki LUB NIE").
            const evaluationNoteIds = Array.isArray(options?.evaluationNoteIds)
                ? options.evaluationNoteIds.filter(Boolean)
                : null;
            // [zgloszenie wlasciciela 2026-09-01, pkt 6] ZAKRES SKLADANIA „za kogos". "dept" = ekran
            // „Moj Dzial", wiec serwer ma sprawdzic przynaleznosc osoby do zasiegu przelozonego NIEZALEZNIE
            // od uprawnien kadrowych. Pole potrafi tylko ZAWEZIC — jego brak nie otwiera niczego nowego.
            const onBehalfScope = options?.onBehalfScope || undefined;
            const dto = {
                typeCode: typeId,
                formDataJson: JSON.stringify(formData),
                targetEmployeeId: targetEmployeeId ? Number(targetEmployeeId) : undefined,
                companyId: companyId ? Number(companyId) : undefined,
                assignmentId: assignmentId || undefined,
                evaluationNoteIds: evaluationNoteIds && evaluationNoteIds.length > 0 ? evaluationNoteIds : undefined,
                onBehalfScope,
            };
            const response = await apiService.createHrRequest(dto);
            const created = mapRequest(response.data);

            // Upload zalacznikow (pliki z pol FILE) — fix: wczesniej byly ciche porzucane.
            const realFiles = (attachments || []).filter((f) => f instanceof File);
            if (created?.id && realFiles.length > 0) {
                try {
                    await apiService.uploadHrRequestAttachments(created.id, realFiles);
                } catch (upErr) {
                    console.error("[hrDataService] upload attachments failed:", upErr);
                    // Nie udawaj sukcesu bez plikow — zglos, ze wniosek utworzony, ale zalaczniki nie doszly.
                    const e = new Error("Wniosek utworzono, ale nie udalo sie wgrac zalacznikow. Sprobuj dodac je ponownie.");
                    e.partial = true; e.request = created;
                    throw e;
                }
            }
            return created;
        } catch (err) {
            console.error("[hrDataService] createRequest failed:", err);
            throw err;
        }
    },

    /**
     * Anuluje wniosek.
     */
    async cancelRequest(requestId, _user, reason = "") {
        try {
            await apiService.cancelHrRequest(requestId, { reason });
            // Return minimal shape for callers that use the result
            return { id: requestId, status: "Cancelled" };
        } catch (err) {
            console.error("[hrDataService] cancelRequest failed:", err);
            throw err;
        }
    },

    /**
     * Zmienia status wniosku (zatwierdzenie / odrzucenie / korekta).
     */
    async changeStatus(requestId, newStatus, user, comment = "", expectedStepOrder = null, dodatkowe = {}) {
        try {
            const dto = {
                newStatus,
                comment: comment || undefined,
                rejectionReason: newStatus?.toLowerCase().includes("rejected") ? comment : undefined,
                correctionReason: newStatus === "CorrectionRequested" ? (comment || undefined) : undefined,
                // [2026-09-14] Korekta wskazuje POLA z uzasadnieniem — lista {fieldKey,label,reason}.
                correctionFields: newStatus === "CorrectionRequested" && Array.isArray(dodatkowe.correctionFields) && dodatkowe.correctionFields.length > 0
                    ? dodatkowe.correctionFields : undefined,
                // FIX audytu: krok, na który patrzył akceptant — backend odrzuci akcję, jeśli stan się przesunął
                // (inny akceptant / podwójne kliknięcie), zamiast zatwierdzać kolejny krok „w ciemno".
                expectedStepOrder: (expectedStepOrder ?? null) === null ? undefined : expectedStepOrder,
            };
            await apiService.changeHrRequestStatus(requestId, dto);
            return { id: requestId, status: newStatus };
        } catch (err) {
            console.error("[hrDataService] changeStatus failed:", err);
            throw err;
        }
    },

    /**
     * HR zada korekty wniosku.
     */
    async requestCorrection(requestId, user, reason, correctionFields = []) {
        return this.changeStatus(requestId, "CorrectionRequested", user, reason, null, { correctionFields });
    },

    /**
     * Przelozony zada korekty wniosku.
     */
    async requestCorrectionByManager(requestId, user, reason, correctionFields = []) {
        return this.changeStatus(requestId, "CorrectionRequested", user, reason, null, { correctionFields });
    },

    /**
     * Pracownik ponownie sklada wniosek po korekcie.
     */
    async resubmitRequest(requestId, user, updatedFormData, odpowiedzNaKorekte = "") {
        try {
            // Resubmit is modelled as a status change with updated formData.
            // [X-G 2026-09-14] `comment` = odpowiedź pracownika na korektę — serwer wymaga jej, gdy któreś ze wskazanych pól nie zmieniło się.
            const dto = {
                newStatus: "Resubmitted",
                comment: (odpowiedzNaKorekte || "").trim(),
                formDataJson: JSON.stringify(updatedFormData),
            };
            await apiService.changeHrRequestStatus(requestId, dto);
            return { id: requestId, status: "Resubmitted" };
        } catch (err) {
            console.error("[hrDataService] resubmitRequest failed:", err);
            throw err;
        }
    },

    // -- DIRECTOR / PRESIDENT HELPERS (pure utility) ----------------------

    isDirector(user) {
        return isDirector(user);
    },

    getPresidentId() {
        // Legacy localStorage-based; keep as lightweight client cache
        return localStorage.getItem("hr_president_id") || null;
    },

    setPresidentId(userId) {
        localStorage.setItem("hr_president_id", userId);
    },

    // -- REQUESTS -- STATS ------------------------------------------------

    async getStats(userId) {
        try {
            // Derive stats from the full (unpaged) request list
            const response = await apiService.getHrRequests({ userId, pageSize: 99999 });
            const items = (response.data?.items || []);
            return {
                total: items.length,
                pending: items.filter((r) => HR_TAB_STATUSES.pending.includes(r.status)).length,
                corrections: items.filter((r) => HR_TAB_STATUSES.corrections.includes(r.status)).length,
                approved: items.filter((r) => HR_TAB_STATUSES.approved.includes(r.status)).length,
                rejected: items.filter((r) => HR_TAB_STATUSES.rejected.includes(r.status)).length,
            };
        } catch (err) {
            console.error("[hrDataService] getStats failed:", err);
            // FIX audytu 2026-08-29 (M-29): wyzerowane kafelki bez `error` wyglądały jak prawdziwe zera.
            return { total: 0, pending: 0, corrections: 0, approved: 0, rejected: 0, error: true };
        }
    },

    /**
     * Zwraca wnioski danego uzytkownika ze statusem CORRECTION_REQUESTED.
     */
    async getCorrectionsForUser(userId) {
        try {
            const response = await apiService.getHrRequests({ userId, status: "CorrectionRequested", pageSize: 200 });
            return (response.data?.items || []).map(mapRequest);
        } catch (err) {
            console.error("[hrDataService] getCorrectionsForUser failed:", err);
            return [];
        }
    },

    // -- BALANCES ---------------------------------------------------------

    async getBalances(userId) {
        try {
            // If userId is provided, use admin endpoint; otherwise use own
            let response;
            if (userId) {
                response = await apiService.getHrUserBalances(userId, new Date().getFullYear());
            } else {
                response = await apiService.getHrBalances();
            }
            return mapBalances(response.data);
        } catch (err) {
            console.error("[hrDataService] getBalances failed:", err);
            return { year: new Date().getFullYear() };
        }
    },

    // Salda członka zespołu widziane PRZEZ PRZEŁOŻONEGO (zakresowy endpoint /my-dept/balances/{id}
    // zamiast admin-only /hr-config/balances/{id}, które nie-adminowi zwraca 403). Ten sam kształt (mapBalances).
    async getScopedBalances(userId) {
        try {
            const response = await apiService.getMyDeptMemberBalances(userId, new Date().getFullYear());
            return mapBalances(response.data);
        } catch (err) {
            console.error("[hrDataService] getScopedBalances failed:", err);
            return { year: new Date().getFullYear() };
        }
    },

    async recalculateBalances(userId) {
        // Backend recalculates automatically; just re-fetch
        return this.getBalances(userId);
    },

    // -- TECZKA (DOCUMENT FILING) -----------------------------------------

    async getTeczkaDocs(userId, section = null) {
        try {
            const params = { userId, section: section || undefined };
            const response = await apiService.getHrTeczkaDocs(params);
            const items = response.data?.items || response.data || [];
            // Normalizacja RAW DTO (jak mapRequest): bez tego podgląd szablonu dokumentu z wniosku był MARTWY —
            // renderTemplate wymaga typeId (DTO ma tylko typeCode) i formData jako obiektu (DTO ma formDataJson=string).
            return (Array.isArray(items) ? items : []).map((d) => {
                let formData = {};
                try { formData = d.formData || (d.formDataJson ? JSON.parse(d.formDataJson) : {}); } catch { formData = {}; }
                return { ...d, typeId: d.typeId || d.typeCode, formData };
            });
        } catch (err) {
            console.error("[hrDataService] getTeczkaDocs failed:", err);
            // FIX audytu 2026-08-29 (M-29): kontrakt to TABLICA, więc flagę awarii wieszamy na niej jako
            // pole nieenumerowalne — konsumenci (`docs || []`, `.map`) działają bez zmian, a ekran, który
            // chce odróżnić „brak dokumentów" od „nie udało się pobrać", czyta `docs.error`.
            // UWAGA: flaga żyje na TEJ referencji — po .filter()/.map() jej nie ma, sprawdzaj od razu.
            const empty = [];
            Object.defineProperty(empty, "error", { value: true, enumerable: false });
            return empty;
        }
    },

    async fileToTeczka(requestId) {
        try {
            await apiService.fileHrToTeczka(requestId);
        } catch (err) {
            console.error("[hrDataService] fileToTeczka failed:", err);
            throw err;
        }
    },

    // -- ROLA KADROWA WOŁAJĄCEGO ------------------------------------------

    /**
     * Rozróżnienie „WIDZI panel kadr" (canView) od „MOŻE DECYDOWAĆ" (canDecide).
     * Awaria = fail-closed na decyzjach: UI nie pokaże przycisków, które serwer i tak odrzuci 403.
     */
    async getMyHrRole() {
        try {
            const response = await apiService.getMyHrRole();
            return {
                canView: !!response.data?.canView,
                canDecide: !!response.data?.canDecide,
                // [HR-SPOLKI, decyzja właściciela 2026-09-27] Spółki z zakresu klucza kadrowego; null = wszystkie spółki.
                zakresSpolek: Array.isArray(response.data?.zakresSpolek) ? response.data.zakresSpolek : null,
            };
        } catch (err) {
            console.error("[hrDataService] getMyHrRole failed:", err);
            return { canView: true, canDecide: false, error: true };
        }
    },

    // -- KOMENTARZE NA WNIOSKU --------------------------------------------

    async getComments(requestId) {
        try {
            const response = await apiService.getHrRequestComments(requestId);
            const items = Array.isArray(response.data) ? response.data : [];
            return items;
        } catch (err) {
            console.error("[hrDataService] getComments failed:", err);
            // Flaga awarii nieenumerowalna na tablicy (ten sam wzorzec co getTeczkaDocs) —
            // konsument odróżnia „brak komentarzy" od „nie udało się pobrać".
            const empty = [];
            Object.defineProperty(empty, "error", { value: true, enumerable: false });
            return empty;
        }
    },

    /**
     * [NOTATKI PRZEŁOŻONEGO 2026-08-30] `audience` rozstrzyga, DOKĄD notatka trafia — wybór należy
     * do piszącego: "hr" (domyślnie) = komentarz jak dotąd, widoczny dla stron wniosku, akceptantów
     * i kadr; "internal" = notatka prywatna przełożonego, widoczna WYŁĄCZNIE dla autora (serwer nie
     * wypuszcza jej nikomu innemu i nie wysyła o niej żadnego powiadomienia).
     */
    async addComment(requestId, content, parentId = null, audience = "hr") {
        try {
            const response = await apiService.addHrRequestComment(requestId, {
                content,
                parentId: parentId || undefined,
                audience: audience === "internal" ? "internal" : "hr",
            });
            return response.data;
        } catch (err) {
            console.error("[hrDataService] addComment failed:", err);
            throw err;
        }
    },

    async deleteComment(requestId, commentId) {
        try {
            await apiService.deleteHrRequestComment(requestId, commentId);
        } catch (err) {
            console.error("[hrDataService] deleteComment failed:", err);
            throw err;
        }
    },

    /**
     * [brief właściciela 2026-08-31, pkt 4.2] „DOCZYTAŁEM" — gasi kropkę przy JEDNYM wniosku.
     *
     * <p>Wołane po otwarciu wątku komentarzy, nie po otwarciu samego wniosku: kropka mówi
     * „ktoś coś napisał", więc gasi ją dopiero przeczytanie wątku.</p>
     *
     * <p>Awaria jest MIĘKKA. Nieudane „doczytałem" nie ma prawa przewrócić otwierania wniosku —
     * użytkownik zobaczy najwyżej kropkę raz za dużo.</p>
     */
    async markCommentsRead(requestId) {
        try {
            await apiService.markHrCommentsRead(requestId);
            return true;
        } catch (err) {
            console.error("[hrDataService] markCommentsRead failed:", err);
            return false;
        }
    },

    /**
     * [brief właściciela 2026-08-31, pkt 4.2] AGREGAT DO KROPKI NA MODULE „Obsługa HR".
     * Zwraca { totalUnread, requestsWithUnread, lastCommentAt } — jedno tanie żądanie zamiast
     * pobierania całej listy wniosków tylko po to, żeby policzyć kropkę.
     */
    async getCommentActivity() {
        try {
            const response = await apiService.getHrCommentActivity();
            const d = response.data || {};
            return {
                totalUnread: d.totalUnread || 0,
                requestsWithUnread: d.requestsWithUnread || 0,
                lastCommentAt: d.lastCommentAt || null,
            };
        } catch (err) {
            console.error("[hrDataService] getCommentActivity failed:", err);
            return { totalUnread: 0, requestsWithUnread: 0, lastCommentAt: null };
        }
    },

    /**
     * [brief właściciela 2026-08-31, pkt 4.3] ZA KOGO WOLNO MI ZŁOŻYĆ WNIOSEK.
     *
     * <p>Jedyne dopuszczalne źródło listy pracowników w kreatorze „w imieniu pracownika".
     * Serwer liczy ją z tego samego zasięgu, którym bramkuje sam zapis, więc przeglądarka nie
     * pokaże osoby, na której POST i tak skończyłby się odmową „Brak uprawnień do złożenia
     * wniosku w imieniu wskazanego pracownika.".</p>
     *
     * <p>ZNANY, ŚWIADOMY KOMPROMIS (opisany przez autora endpointu): dla administratora lista
     * bywa WĘŻSZA niż to, co serwer realnie przyjmie. Rozjazd jest w stronę bezpieczną — nigdy
     * nie omija bramki.</p>
     */
    /**
     * [zgloszenie wlasciciela 2026-09-01, pkt 6] `zakres` = ekran, ktory pyta. "dept" oznacza „Moj Dzial",
     * czyli: lista ma zawierac WYLACZNIE osoby z zasiegu przelozonego — takze wtedy, gdy pytajacy jest
     * kadrowa albo administratorem. Bez tego kreator na /moj-dzial/wnioski pokazywal cala firme pod
     * naglowkiem „Wybierz pracownika ze swojego dzialu".
     */
    async getOnBehalfCandidates(search = "", zakres = null) {
        try {
            const response = await apiService.getHrOnBehalfCandidates(search, zakres);
            const items = Array.isArray(response.data) ? response.data : [];
            // Kształt ujednolicony z listą zespołu (`employees`), żeby krok „Pracownik" renderował
            // jedną kartą obie drogi wejścia — a nie dwiema, które rozjadą się przy pierwszej zmianie.
            return items.map((o) => ({
                id: o.id,
                userId: o.id,
                userName: o.name,
                employeeNumber: o.employeeNumber || null,
                /* [O2 2026-09-13] Przy 2+ działach odpowiedzialności serwer dokłada `dzial` — dział z przełącznika „Mój Dział”.
                   Etykieta pokazuje wtedy obie nazwy (jednostka · dział odpowiedzialności), gdy się różnią. */
                unitName: o.dzial && o.dzial !== o.department
                    ? [o.department, o.dzial].filter(Boolean).join(" · ")
                    : (o.department || null),
                departmentName: o.dzial || null,
                /* [polecenie właściciela 2026-09-06, pkt 5] BYŁO ŹLE: `jobTitle: null` na sztywno.
               Nawet gdyby serwer odesłał stanowisko, ten mapper by je zgubił — a razem z nim
               przestawała działać gotowa podpowiedź stanowiska w formularzu wniosku
               (ManagerRequestDrawer: `if (emp.jobTitle) pre.currentPosition = emp.jobTitle`),
               która przez to nigdy się nie odpalała. */
            jobTitle: o.jobTitle || null,
            }));
        } catch (err) {
            console.error("[hrDataService] getOnBehalfCandidates failed:", err);
            return [];
        }
    },

    // -- AUDIT LOG --------------------------------------------------------

    async getAuditLog(filters = {}) {
        try {
            const params = {
                requestId: filters.requestId,
                userId: filters.userId,
                pageSize: filters.limit || 50,
            };
            const response = await apiService.getHrAuditLog(params);
            return response.data?.items || response.data || [];
        } catch (err) {
            console.error("[hrDataService] getAuditLog failed:", err);
            return [];
        }
    },

    /**
     * Rejestruje interakcje pracownika (PAGE_VIEWED / DETAIL_OPENED / FORM_SUBMITTED /
     * REQUEST_APPROVED / REQUEST_REJECTED / CORRECTION_REQUESTED ...) do audytu HR.
     * Buforuje zdarzenia i wysyla je wsadowo (best-effort) — nie blokuje UI i nie rzuca bledami.
     */
    logInteraction(action, _user, details = {}, requestId = null) {
        try {
            if (!action) return;
            hrInteractionBuffer.push({
                action: String(action).slice(0, 50),
                requestId: requestId || (details && details.requestId) || null,
                detailsJson: safeStringify(details),
                clientTimestamp: new Date().toISOString(),
            });
            if (hrInteractionBuffer.length >= INTERACTION_FLUSH_SIZE) {
                flushHrInteractions();
            } else {
                scheduleHrInteractionFlush();
            }
        } catch { /* logowanie nie moze psuc UI */ }
    },

    // -- TEAM / MANAGER QUERIES -------------------------------------------

    async getPendingForManager(_managerId, filters = {}) {
        try {
            const params = {
                pageNumber: filters.page || 1,
                pageSize: filters.pageSize || 20,
                searchQuery: filters.search,
            };
            const response = await apiService.getHrPendingApprovals(params);
            return mapListResult(response.data);
        } catch (err) {
            console.error("[hrDataService] getPendingForManager failed:", err);
            return { items: [], totalCount: 0 };
        }
    },

    /**
     * [zgłoszenie właściciela 2026-09-03, poz. 1d] Ile spraw czeka na moją decyzję JAKO KADR — czyli
     * ile wypadło z kolejki „Mój Dział / Do akceptacji" i stoi w Obsłudze HR. Zero (także przy błędzie),
     * gdy nie mam roli kadrowej: wtedy komunikat o Obsłudze HR w ogóle się nie pokazuje.
     */
    async getPendingKadroweCount() {
        try {
            const response = await apiService.getHrPendingKadroweCount();
            return Number(response.data?.count) || 0;
        } catch (err) {
            console.error("[hrDataService] getPendingKadroweCount failed:", err);
            return 0;
        }
    },

    async getPendingForHr(filters = {}) {
        try {
            const params = {
                pageNumber: filters.page || 1,
                pageSize: filters.pageSize || 20,
                searchQuery: filters.search,
            };
            const response = await apiService.getHrPendingApprovals(params);
            return mapListResult(response.data);
        } catch (err) {
            console.error("[hrDataService] getPendingForHr failed:", err);
            return { items: [], totalCount: 0 };
        }
    },

    async getTeamLeave(_managerId, filters = {}) {
        try {
            const params = {
                pageNumber: filters.page || 1,
                pageSize: filters.pageSize || 50,
            };
            const response = await apiService.getHrTeamRequests(params);
            return mapListResult(response.data);
        } catch (err) {
            console.error("[hrDataService] getTeamLeave failed:", err);
            return { items: [], totalCount: 0 };
        }
    },

    async getManagerStats(_managerId) {
        try {
            const response = await apiService.getHrPendingApprovals({ pageSize: 99999 });
            const items = response.data?.items || [];
            const pendingManager = items.filter((r) =>
                ["PendingManager", "PendingDirector"].includes(r.status)
            ).length;
            const pendingHr = items.filter((r) => r.status === "PendingHr").length;
            const today = new Date().toISOString().slice(0, 10);
            const approvedToday = items.filter((r) =>
                r.managerApprovalDate && r.managerApprovalDate.startsWith(today)
            ).length;
            return { pendingManager, pendingHr, approvedToday };
        } catch (err) {
            console.error("[hrDataService] getManagerStats failed:", err);
            return { pendingManager: 0, pendingHr: 0, approvedToday: 0 };
        }
    },

    // -- DEPARTMENT-SCOPED QUERIES ----------------------------------------

    // Wspólne parametry filtrów/sortowania forwardowane do GET /hr-requests/team.
    // filterEmployee = userId (odporne na imienników), filterType = typeCode.
    _teamParams(filters = {}) {
        return {
            searchQuery: filters.search || undefined,
            typeCode: filters.filterType || undefined,
            employeeUserId: filters.filterEmployee || undefined,
            sortField: filters.sortField || undefined,
            sortDir: filters.sortDir || undefined,
            pageNumber: filters.page || 1,
            pageSize: filters.pageSize || 15,
        };
    },

    async getRequestsForManagerApproval(_managerId, _memberIds, filters = {}) {
        try {
            const params = { status: "PendingManager,PendingDirector", ...this._teamParams(filters) };
            const response = await apiService.getHrTeamRequests(params);
            return mapListResult(response.data);
        } catch (err) {
            console.error("[hrDataService] getRequestsForManagerApproval failed:", err);
            return { items: [], totalCount: 0 };
        }
    },

    /**
     * [BRIEF 2026-08-30 pkt 2] „CO CZEKA NA MOJĄ DECYZJĘ" — kanoniczna kolejka GET /hr-requests/pending
     * (HrService.GetPendingForApprovalAsync: role rozstrzygane NA ŻYWO + drzewo działów).
     *
     * DLACZEGO OSOBNA METODA: `getRequestsForManagerApproval` woła /hr-requests/team ze statusem
     * „PendingManager,PendingDirector", czyli odpowiada na INNE pytanie — „wnioski mojego zespołu
     * w statusie X". To dwa różne silniki zakresu, więc licznik na Pulpicie (który liczy z /pending)
     * i lista w zakładce potrafiły pokazać różne rzeczy: osoba wskazana ROLĄ widziała wniosek
     * w kaflu, a nie widziała go na liście — i odwrotnie.
     *
     * OGRANICZENIE: /hr-requests/pending przyjmuje wyłącznie stronicowanie, bez filtrów i sortowania.
     * Kolejka decyzji jest z natury krótka, więc pobieramy ją jednym żądaniem i filtrujemy tutaj —
     * zamiast pokazywać szybciej, ale nie to, o co pyta użytkownik.
     */
    async getPendingForMyDecision(filters = {}) {
        try {
            const response = await apiService.getHrPendingApprovals({ pageNumber: 1, pageSize: 200 });
            const mapped = mapListResult(response.data);
            let items = mapped.items;

            const szukaj = (filters.search || "").trim().toLowerCase();
            if (szukaj) {
                items = items.filter((r) =>
                    (r.userName || "").toLowerCase().includes(szukaj)
                    || (r.targetEmployeeName || "").toLowerCase().includes(szukaj)
                    || (r.requestNumber || "").toLowerCase().includes(szukaj)
                    || (r.typeLabel || "").toLowerCase().includes(szukaj)
                );
            }
            if (filters.filterType) items = items.filter((r) => r.typeId === filters.filterType);
            if (filters.filterEmployee) {
                const uid = String(filters.filterEmployee);
                items = items.filter((r) =>
                    String(r.targetEmployeeId ?? r.userId) === uid || String(r.userId) === uid
                );
            }
            return { items, totalCount: items.length };
        } catch (err) {
            console.error("[hrDataService] getPendingForMyDecision failed:", err);
            return { items: [], totalCount: 0 };
        }
    },

    async getRequestsForHrView(_managerId, _memberIds, filters = {}) {
        try {
            const params = { status: "PendingHr", ...this._teamParams(filters) };
            const response = await apiService.getHrTeamRequests(params);
            return mapListResult(response.data);
        } catch (err) {
            console.error("[hrDataService] getRequestsForHrView failed:", err);
            return { items: [], totalCount: 0 };
        }
    },

    async getRequestHistory(_managerId, _memberIds, filters = {}) {
        try {
            // Historia: status wybierany przez użytkownika (wcześniej w ogóle nie forwardowany → filtr martwy).
            const params = { status: filters.filterStatus || undefined, ...this._teamParams(filters) };
            const response = await apiService.getHrTeamRequests(params);
            return mapListResult(response.data);
        } catch (err) {
            console.error("[hrDataService] getRequestHistory failed:", err);
            return { items: [], totalCount: 0 };
        }
    },

    /**
     * [zgloszenie wlasciciela 2026-09-01, pkt 7d] „Chce widziec statusy wnioskow, ktore lataja w sprawie
     * mojego dzialu" — liczniki kategorii statusow z serwera.
     *
     * DLACZEGO NIE ZLICZAMY W PRZEGLADARCE: strona listy zespolu jest przycinana SERWEROWO do 200 wierszy
     * (HrRequestQueryParams.MaxPageSize), wiec `pageSize: 99999` i tak zwraca najwyzej 200 — od 201. wniosku
     * kazdy licznik liczony u klienta po cichu klamalby zanizona wartoscia. Endpoint liczy GROUP BY w bazie,
     * na tym samym zasiegu zespolu, ktorym filtrowana jest lista pod kategoriami.
     *
     * Zwraca `{}` przy awarii — kategorie pokaza zera zamiast wywrocic ekran.
     */
    async getTeamStatusCounts() {
        try {
            const response = await apiService.getHrTeamStatusCounts();
            return response.data && typeof response.data === "object" ? response.data : {};
        } catch (err) {
            console.error("[hrDataService] getTeamStatusCounts failed:", err);
            return {};
        }
    },

    async getManagerStatsDept(_managerId, _memberIds) {
        try {
            const response = await apiService.getHrTeamRequests({ pageSize: 99999 });
            const items = response.data?.items || [];
            const pendingManager = items.filter((r) =>
                ["PendingManager", "PendingDirector"].includes(r.status)
            ).length;
            const pendingHr = items.filter((r) => r.status === "PendingHr").length;
            const today = new Date().toISOString().slice(0, 10);
            const approvedByManager = items.filter((r) =>
                r.managerApprovalDate && r.managerApprovalDate.startsWith(today)
            ).length;
            const correctionRequested = items.filter((r) => r.status === "CorrectionRequested").length;
            const completedTotal = items.filter((r) => r.status === "Completed").length;
            const totalRequests = items.length;
            return { pendingManager, pendingHr, approvedByManager, correctionRequested, completedTotal, totalRequests };
        } catch (err) {
            console.error("[hrDataService] getManagerStatsDept failed:", err);
            return { pendingManager: 0, pendingHr: 0, approvedByManager: 0, correctionRequested: 0, completedTotal: 0, totalRequests: 0 };
        }
    },

    // -- HR MANAGEMENT (all requests) -------------------------------------

    async getAllRequests(filters = {}) {
        try {
            const params = {
                status: filters.status,
                category: filters.category,
                typeCode: filters.typeId,
                searchQuery: filters.search,
                pageNumber: filters.page || 1,
                pageSize: filters.pageSize || 20,
            };
            const response = await apiService.getHrRequests(params);
            const data = response.data;
            return {
                items: (data?.items || []).map(mapRequest),
                total: data?.totalCount ?? data?.total ?? 0,
                page: data?.page ?? params.pageNumber,
                pageSize: data?.pageSize ?? params.pageSize,
                totalPages: data?.totalPages ?? Math.ceil((data?.totalCount ?? 0) / params.pageSize),
            };
        } catch (err) {
            console.error("[hrDataService] getAllRequests failed:", err);
            // `error` pozwala odróżnić AWARIĘ od pustej listy — bez tego widok obsługi HR
            // pokazywał „Brak wniosków" także wtedy, gdy API zwróciło błąd.
            return { items: [], total: 0, page: 1, pageSize: 20, totalPages: 0, error: true };
        }
    },

    async getHrStats() {
        try {
            const response = await apiService.getHrRequests({ pageSize: 99999 });
            const items = response.data?.items || [];
            const today = new Date().toISOString().slice(0, 10);
            return {
                total: items.length,
                pendingHr: items.filter((r) => r.status === "PendingHr").length,
                pendingManager: items.filter((r) => ["PendingManager", "PendingDirector", "PendingCfo", "PendingPresident", "PendingEmployeeAck"].includes(r.status)).length,
                correctionRequested: items.filter((r) => r.status === "CorrectionRequested").length,
                completedToday: items.filter((r) => r.status === "Completed" && r.completedAt?.startsWith(today)).length,
                rejected: items.filter((r) => ["Rejected", "RejectedByManager", "RejectedByDirector", "RejectedByCfo", "RejectedByPresident"].includes(r.status)).length,
            };
        } catch (err) {
            console.error("[hrDataService] getHrStats failed:", err);
            return { total: 0, pendingHr: 0, pendingManager: 0, correctionRequested: 0, completedToday: 0, rejected: 0 };
        }
    },

    async bulkApprove(requestIds, _hrUserId, comment = "") {
        try {
            let count = 0;
            for (const id of requestIds) {
                try {
                    await apiService.changeHrRequestStatus(id, {
                        newStatus: "Completed",
                        comment,
                    });
                    count++;
                } catch {
                    // Skip individual failures
                }
            }
            return count;
        } catch (err) {
            console.error("[hrDataService] bulkApprove failed:", err);
            return 0;
        }
    },

    // -- APPROVED REMOTE REQUESTS -----------------------------------------

    async getApprovedRemoteRequestsForDate(userId, dateISO) {
        try {
            const response = await apiService.getHrRequests({
                userId,
                typeCode: "REMOTE_OCCASIONAL",
                status: "Completed",
                pageSize: 100,
            });
            const items = (response.data?.items || []).map(mapRequest);
            return items.filter((r) => r.formData?.date === dateISO);
        } catch (err) {
            console.error("[hrDataService] getApprovedRemoteRequestsForDate failed:", err);
            return [];
        }
    },

    async getLastRequestOfType(userId, typeId) {
        try {
            const response = await apiService.getHrRequests({
                userId,
                typeCode: typeId,
                pageSize: 1,
                pageNumber: 1,
            });
            const items = (response.data?.items || []).map(mapRequest);
            return items[0] || null;
        } catch (err) {
            console.error("[hrDataService] getLastRequestOfType failed:", err);
            return null;
        }
    },

    // =====================================================================
    // EVALUATIONS -- backend endpoints (HrController)
    // =====================================================================

    async createEvaluation(user, employeeId, employeeName, data) {
        const response = await apiService.createHrEvaluation({
            employeeId,
            employeeName,
            title: data.title,
            content: data.content,
            category: data.category,
        });
        return response.data;
    },

    async updateEvaluation(evaluationId, data) {
        const response = await apiService.updateHrEvaluation(evaluationId, {
            title: data.title,
            content: data.content,
            category: data.category,
        });
        return response.data;
    },

    async approveEvaluation(evaluationId, _directorUser) {
        const response = await apiService.approveHrEvaluation(evaluationId);
        return response.data;
    },

    async deleteEvaluation(evaluationId, _user) {
        await apiService.deleteHrEvaluation(evaluationId);
    },

    // [brief właściciela 2026-08-30] USUNIĘTE: sendEvaluationToHr (kanał „prześlij samą notatkę do HR").
    // Notatka nie tworzyła żadnego wniosku, więc w kadrach nie było czego zaakceptować ani przyjąć do
    // wiadomości — stąd „wysłałem, a nie doszło". Droga do kadr prowadzi teraz WYŁĄCZNIE przez wniosek
    // „Ocena realizacji obowiązków na etapie wdrożenia" (createRequest + options.evaluationNoteIds).
    // Nie przywracaj tej metody: byłby to powrót do dwóch obiegów o tej samej nazwie.

    async getEvaluations(employeeId) {
        try {
            const response = await apiService.getHrEvaluations(employeeId);
            return response.data || [];
        } catch (err) {
            console.error("[hrDataService] getEvaluations failed:", err);
            return [];
        }
    },

    async getEvaluationStats(employeeIds) {
        try {
            const response = await apiService.getHrEvaluationStats(employeeIds);
            return response.data || {};
        } catch (err) {
            console.error("[hrDataService] getEvaluationStats failed:", err);
            return {};
        }
    },

    // =====================================================================
    // VACATION PLANS -- backend endpoints (VacationPlanController)
    // =====================================================================

    /* [BLAD Z DZIENNIKA PRODUKCJI 2026-09-12] Wszystkie piec funkcji planow urlopowych podstawialo ZLY
       identyfikator. Koncowki serwera maja ksztalt `/departments/{deptId}/vacation-plans...`, a tu leciala
       kolejno: tozsamosc ZALOGOWANEJ OSOBY, identyfikator PLANU, caly OBIEKT, albo nic. Adres sklejal sie
       wiec jako `/departments/3/...`, `/departments/[object Object]/...`, `/departments/undefined/...`,
       serwer odrzucal zadanie na wiazaniu modelu, a `catch` to polykal.
       SKUTEK DLA CZLOWIEKA: baner „masz zatwierdzony plan urlopowy" NIGDY sie nie pokazywal, a nikt tego nie
       zglosil, bo nic sie nie wywracalo — po prostu cicho nie dzialalo.

       Naprawa jest DWUCZESCIOWA, bo sam poprawny identyfikator dzialu by nie wystarczyl: koncowka dzialowa
       stoi za bramka PRZELOZONEGO i zwraca plany CALEGO dzialu. Zwykly pracownik dostalby 403, a gdyby dostal
       odpowiedz — zobaczylby plany urlopowe kolegow. Dlatego:
       • pytanie o WLASNY plan idzie nowa koncowka `/api/moj-plan-urlopowy` (bez identyfikatora, wiec bez IDOR-u),
       • funkcje DZIALOWE wymagaja teraz `deptId` WPROST i odmawiaja wykonania bez niego, zamiast sklejac adres
         z czegokolwiek, co akurat dostana. */

    async getMojPlanUrlopowy(rok) {
        try {
            const response = await apiService.getMojPlanUrlopowy(rok || new Date().getFullYear());
            return response.data || null;
        } catch (err) {
            console.error("[hrDataService] getMojPlanUrlopowy failed:", err);
            return null;
        }
    },

    async getVacationPlan(deptId, year) {
        if (!deptId) {
            console.error("[hrDataService] getVacationPlan wymaga identyfikatora DZIALU. "
                + "Do wlasnego planu uzyj getMojPlanUrlopowy().");
            return [];
        }
        try {
            const response = await apiService.getVacationPlans(deptId, year);
            return response.data || [];
        } catch (err) {
            console.error("[hrDataService] getVacationPlan failed:", err);
            return [];
        }
    },

    async saveVacationPlan(deptId, year, planData) {
        if (!deptId) throw new Error("saveVacationPlan wymaga identyfikatora dzialu (deptId).");
        const response = await apiService.saveVacationPlan(deptId, { year: year || new Date().getFullYear(), ...planData });
        return response.data;
    },

    async approveVacationPlan(deptId, planId) {
        if (!deptId || !planId) throw new Error("approveVacationPlan wymaga deptId i planId.");
        const response = await apiService.approveVacationPlan(deptId, planId);
        return response.data;
    },

    async rejectVacationPlan(deptId, planId, reason) {
        if (!deptId || !planId) throw new Error("rejectVacationPlan wymaga deptId i planId.");
        const response = await apiService.rejectVacationPlan(deptId, planId, { reason });
        return response.data;
    },

    // -- WITHDRAWAL (wycofanie wniosku) ------------------------------------
    // Domyślne okno wycofania zrealizowanego wniosku — lustro HrWithdrawalPolicy.DomyslneOknoDni.

    /**
     * Zwraca listę wniosków kwalifikujących się do wycofania:
     * - status Completed
     * - nie starsze niż 14 dni (od completedAt)
     * - nie typu WITHDRAWAL
     * - bez aktywnego (niezakończonego) wniosku wycofania
     */
    async getEligibleForWithdrawal(userId) {
        try {
            const { items } = await this.getMyRequests(userId, { status: "Completed", pageSize: 200 });
            // [P1] JEDNA reguła kwalifikacji — ta sama, która decyduje o przycisku w tabeli
            // i której pilnuje serwer. Dotąd okno 14 dni było tu przepisane drugi raz.
            const eligible = items.filter((r) => this.isEligibleForWithdrawal(r));

            // Wykluczenie wniosków, które już mają aktywne wycofanie
            const { items: allReqs } = await this.getMyRequests(userId, { pageSize: 200 });
            const pendingWithdrawalIds = new Set(
                allReqs
                    .filter(w => w.typeId === "WITHDRAWAL"
                        && !["Cancelled", "Rejected", "RejectedByManager", "RejectedByDirector"].includes(w.status))
                    .map(w => w.formData?.originalRequestId)
                    .filter(Boolean)
            );

            return eligible.filter(r => !pendingWithdrawalIds.has(r.id));
        } catch (err) {
            console.error("[hrDataService] getEligibleForWithdrawal:", err);
            return [];
        }
    },

    /**
     * Sprawdza, czy dany wniosek kwalifikuje się do formalnego WYCOFANIA (synchronicznie).
     * Używane w tabeli do wyświetlania przycisku „Wycofaj".
     *
     * [brief właściciela 2026-08-31, pkt P1] LUSTRO REGUŁ SERWERA (HrWithdrawalPolicy):
     *  - typ wniosku może mieć wycofanie WYŁĄCZONE (allowWithdrawal = false),
     *  - okno wycofania jest KONFIGUROWALNE per typ (withdrawalWindowDays; puste = 14 dni, 0 = brak okna),
     *  - wniosek, którego okres właśnie TRWA, nie podlega wycofaniu.
     *
     * ⚠️ To tylko widoczność przycisku. Decyzję podejmuje serwer — od tej wersji sprawdza te
     * reguły przy składaniu wniosku WITHDRAWAL (wcześniej okno 14 dni żyło wyłącznie tutaj
     * i dawało się je ominąć, pomijając interfejs).
     */
    isEligibleForWithdrawal(request) {
        if (!request || request.status !== "Completed") return false;
        if (request.typeId === "WITHDRAWAL" || request.typeCode === "WITHDRAWAL") return false;
        if (request.allowWithdrawal === false) return false;
        if (this.isRequestInProgress(request)) return false;

        const okno = Number.isFinite(Number(request.withdrawalWindowDays))
            ? Math.max(0, Number(request.withdrawalWindowDays))
            : DOMYSLNE_OKNO_WYCOFANIA_DNI;
        if (okno <= 0) return false;

        const completedAt = request.completedAt ? new Date(request.completedAt) : null;
        // Brak daty realizacji (wnioski sprzed naprawy A05-05) nie blokuje — lustro serwera.
        if (!completedAt) return true;
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - okno);
        return completedAt >= cutoff;
    },

    /**
     * Czy okres, którego wniosek dotyczy, WŁAŚNIE TRWA (zaczął się i jeszcze nie minął).
     * Lustro HrWithdrawalPolicy.WTrakcieTrwania. Brak dat = nie trwa (wniosek bezterminowy).
     */
    isRequestInProgress(request) {
        const fd = request?.formData || {};
        const od = String(fd.dateFrom || fd.date || "").slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(od)) return false;
        const dzis = new Date().toISOString().slice(0, 10);
        if (od > dzis) return false;
        const doStr = String(fd.dateTo || fd.dateFrom || fd.date || "").slice(0, 10);
        const koniec = /^\d{4}-\d{2}-\d{2}$/.test(doStr) ? doStr : od;
        return koniec >= dzis;
    },

    /**
     * Czy PRACOWNIK (autor, nie-HR) może SAM wycofać wniosek przez bezpośrednie „Anuluj/Wycofaj".
     * Lustro reguły backendu (CanEmployeeSelfCancel): tylko wniosek jeszcze niezaakceptowany
     * (czysto procesowany) LUB planowany urlop przed startem. Zaakceptowany/zrealizowany = tylko Kadry.
     */
    canEmployeeWithdraw(request) {
        if (!request) return false;
        const status = String(request.status || "");
        if (status === "Cancelled") return false;
        // Planowany urlop przed rozpoczęciem — plan swobodnie odwoływalny (nawet po hr_only).
        const isPlanned = request.typeId === "PLANNED_VACATION" || request.typeCode === "PLANNED_VACATION";
        if (isPlanned) {
            const fd = request.formData || {};
            const start = fd.dateFrom || fd.date;
            // Porównanie po dacie (YYYY-MM-DD, UTC) — zgodne z backendem (DateTime.UtcNow.Date).
            // Brak/niepoprawna data → traktuj jak rozpoczęty (nie omijaj kontroli).
            const startStr = start ? String(start).slice(0, 10) : "";
            const validStart = /^\d{4}-\d{2}-\d{2}$/.test(startStr);
            const todayUtc = new Date().toISOString().slice(0, 10);
            const started = !validStart || startStr <= todayUtc;
            if (!started) return true;
        }
        // [P1] Typ z wyłączonym wycofaniem — przycisku nie ma żadną drogą.
        if (request.allowWithdrawal === false) return false;
        if (status === "Completed") return false;
        // Zaakceptowany na którymś kroku? (elastyczne kroki mają pierwszeństwo, inaczej status legacy)
        const steps = Array.isArray(request.approvalSteps) ? request.approvalSteps : [];
        if (steps.length > 0) {
            if (steps.some((s) => Number(s.status) === 1)) return false; // 1 = Approved
        } else if ([
            "ApprovedByManager", "ApprovedByDirector", "ApprovedByCfo", "ApprovedByPresident",
            "PendingDirector", "PendingHr", "PendingCfo", "PendingPresident",
        ].includes(status)) {
            return false;
        }
        // [P1] Właściciel: „jak jest zaakceptowany albo w trakcie trwania, nie można go wycofać".
        if (this.isRequestInProgress(request)) return false;
        return true;
    },

    async getPendingVacationPlansForManager(deptId) {
        // Patrz wyjasnienie wyzej: bez identyfikatora dzialu adres sklejal sie jako
        // `/departments/undefined/vacation-plans/pending`.
        if (!deptId) {
            console.error("[hrDataService] getPendingVacationPlansForManager wymaga identyfikatora DZIALU.");
            return [];
        }
        try {
            const response = await apiService.getPendingVacationPlans(deptId);
            return response.data || [];
        } catch (err) {
            console.error("[hrDataService] getPendingVacationPlansForManager failed:", err);
            return [];
        }
    },
};

export default hrDataService;
